import { Api } from "../Api";

/**
 * Fetch all audio snippets with optional filters
 * @param {Object} options - Query options
 * @param {number} options.skip - Pagination offset
 * @param {number} options.limit - Maximum number of results
 * @param {string} options.start_date - Filter by start date (ISO format)
 * @param {string} options.end_date - Filter by end date (ISO format)
 * @returns {Promise<Array>} Array of audio snippets
 */
export const fetchAudioSnippets = async (options = {}) => {
  try {
    const { skip = 0, limit = 100, start_date, end_date, user_id } = options;
    
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    
    if (start_date) {
      params.append('start_date', start_date);
    }
    if (end_date) {
      params.append('end_date', end_date);
    }
    if (user_id !== undefined) {
      params.append('user_id', user_id.toString());
    }
    
    const response = await Api.get(`/audio_snippets?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching audio snippets:", error);
    throw error;
  }
};

/**
 * Fetch a single audio snippet by ID
 * @param {number} audioSnippetId - Audio snippet ID
 * @param {AbortSignal} signal - Optional AbortSignal for canceling the request
 * @returns {Promise<Object>} Audio snippet details
 */
export const fetchAudioSnippet = async (audioSnippetId, signal) => {
  try {
    const response = await Api.get(`/audio_snippets/${audioSnippetId}`, { signal });
    return response.data;
  } catch (error) {
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      // Silently ignore abort errors
      return null;
    }
    console.error(`Error fetching audio snippet ${audioSnippetId}:`, error);
    throw error;
  }
};

/**
 * Get signed URL for audio playback
 * @param {number} audioSnippetId - Audio snippet ID
 * @param {string} audioType - Type of audio ('filtered' or 'unfiltered')
 * @param {AbortSignal} signal - Optional AbortSignal for canceling the request
 * @returns {Promise<string>} Signed URL for audio playback
 */
export const getAudioSnippetUrl = async (audioSnippetId, audioType = "unfiltered", signal) => {
  try {
    const response = await Api.get(
      `/audio_snippets/${audioSnippetId}/audio?audio_type=${audioType}`,
      { signal }
    );
    return response.data.signed_url;
  } catch (error) {
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      // Silently ignore abort errors
      return null;
    }
    console.error(`Error getting audio URL for snippet ${audioSnippetId}:`, error);
    throw error;
  }
};

